import React from 'react';
import { Box, Button, Popover, TextField } from '@mui/material';

const toPickerValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const date = value >= 1000 && value <= 9999 ? new Date(value, 0, 1) : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string' && /^\d{4}$/.test(value.trim())) {
    return new Date(Number(value), 0, 1);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const pad = (value) => String(value).padStart(2, '0');

const toDateInputValue = (value) => {
  const date = toPickerValue(value);
  if (!date) return '';

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const toDateTimeInputValue = (value) => {
  const date = toPickerValue(value);
  if (!date) return '';

  return `${toDateInputValue(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toYearInputValue = (value) => {
  const date = toPickerValue(value);
  return date ? String(date.getFullYear()) : '';
};

const parseDateInputValue = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const parseDateTimeInputValue = (value) => {
  if (!value) return null;

  const [datePart, timePart = '00:00'] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day, hours || 0, minutes || 0);
};

const parseYearInputValue = (value) => {
  if (!value || !/^\d{4}$/.test(value)) return null;
  return new Date(Number(value), 0, 1);
};

const DatePickerWrapper = ({
  value,
  onChange,
  label,
  inputVariant,
  invalidDateMessage,
  autoOk,
  disableToolbar,
  variant,
  input,
  fullWidth,
  margin,
  error,
  helperText,
  sx,
  style,
  slotProps,
  views,
  format,
  disableFuture,
  disablePast,
  disabled,
  readOnly,
  minDate,
  maxDate,
  className,
  name,
  id,
  required,
  onBlur,
  onFocus,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isYearOnly = views?.length === 1 && views[0] === 'year';
  const isDateTime = !isYearOnly && format?.toLowerCase?.().includes('hh');
  const inputValue = isYearOnly ? toYearInputValue(value) : isDateTime ? toDateTimeInputValue(value) : toDateInputValue(value);
  const today = toDateInputValue(new Date());
  const currentYear = new Date().getFullYear();
  const selectedYear = toPickerValue(value)?.getFullYear();
  const maxYear = maxDate?.getFullYear?.() || (disableFuture ? currentYear : currentYear + 10);
  const minYear = minDate?.getFullYear?.() || currentYear - 60;
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, index) => maxYear - index);

  const openYearPicker = (event) => {
    if (disabled || readOnly || !isYearOnly) return;
    setAnchorEl(event.currentTarget);
  };

  const closeYearPicker = () => setAnchorEl(null);

  const handleYearSelect = (year) => {
    onChange?.(new Date(year, 0, 1));
    closeYearPicker();
  };

  const field = (
    <TextField
      label={label}
      value={inputValue}
      onChange={(event) => {
        const nextValue = event.target.value;
        onChange?.(
          isYearOnly
            ? parseYearInputValue(nextValue)
            : isDateTime
              ? parseDateTimeInputValue(nextValue)
            : parseDateInputValue(nextValue)
        );
      }}
      onClick={openYearPicker}
      type={isYearOnly ? 'text' : isDateTime ? 'datetime-local' : 'date'}
      placeholder={isYearOnly ? 'YYYY' : undefined}
      className={className}
      name={name}
      id={id}
      required={required}
      onBlur={onBlur}
      onFocus={onFocus}
      fullWidth={fullWidth}
      margin={margin}
      error={error}
      helperText={error ? helperText || invalidDateMessage : helperText}
      variant={inputVariant || 'outlined'}
      style={style}
      disabled={disabled}
      {...slotProps?.textField}
      sx={{
        ...sx,
        ...(isYearOnly
          ? {
              '& .MuiInputBase-input': {
                cursor: disabled || readOnly ? 'default' : 'pointer',
              },
            }
          : {}),
        ...slotProps?.textField?.sx,
      }} slotProps={{ input: isYearOnly ? { readOnly: true } : undefined, htmlInput: {
        readOnly,
        min: !isYearOnly && minDate ? toDateInputValue(minDate) : undefined,
        max: !isYearOnly
          ? maxDate
              ? toDateInputValue(maxDate)
              : disableFuture
                ? today
                : undefined
          : undefined,
      }, inputLabel: !isYearOnly ? { shrink: true } : undefined }}
    />
  );

  if (!isYearOnly) return field;

  return (
    <>
      {field}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeYearPicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: {
          sx: {
            mt: 1,
            p: 1.25,
            width: 292,
            maxHeight: 310,
            overflowY: 'auto',
            borderRadius: '10px',
            border: '1px solid #dbe5ef',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.16)',
          },
        } }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 0.75,
          }}
        >
          {years.map((year) => (
            <Button
              key={year}
              size="small"
              variant={year === selectedYear ? 'contained' : 'text'}
              onClick={() => handleYearSelect(year)}
              sx={{
                minWidth: 0,
                borderRadius: '8px',
                fontWeight: 800,
                color: year === selectedYear ? '#fff' : '#0f172a',
                backgroundColor: year === selectedYear ? '#03407b' : 'transparent',
                '&:hover': {
                  backgroundColor: year === selectedYear ? '#02357a' : '#ebf4fb',
                },
              }}
            >
              {year}
            </Button>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default DatePickerWrapper;
